/**
 * API Data Transformation Utilities
 *
 * Bridges backend response shapes to UI-friendly structures.
 */

import type {
  GetUserMerchantsResponseType,
  ListCouponsResponseType,
  ListMembersResponseType,
  ListRedemptionsResponseType,
  ListWalletCouponsResponseType,
} from '@zlinkx/shared-v2';
import { TimestampService } from '@zlinkx/shared-v2';
import type {
  CouponWithComputed,
  MerchantCouponApi,
  MerchantMemberApi,
  MerchantMembershipApi,
  TransformedCouponRedemption,
  TransformedMember,
  TransformedMerchant,
  TransformedMerchantCoupon,
  TransformedUserCoupon,
} from '@/types/api-transformers.types';
export type {
  CouponWithComputed,
  MerchantCouponApi,
  MerchantMemberApi,
  MerchantMembershipApi,
  TransformedCouponRedemption,
  TransformedMember,
  TransformedMerchant,
  TransformedMerchantCoupon,
  TransformedUserCoupon,
} from '@/types/api-transformers.types';

function hasDataArray<T>(value: unknown): value is { data: T[] } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    Array.isArray((value as { data: unknown }).data)
  );
}

function toIsoString(value: Date | string | number | null): string | null {
  if (value === null) {
    return null;
  }

  if (value instanceof Date) {
    return TimestampService.isValidDate(value) ? value.toISOString() : null;
  }

  if (typeof value === 'number') {
    const milliseconds = value < 10_000_000_000 ? value * 1000 : value;
    const date = TimestampService.getCurrentDate();
    date.setTime(milliseconds);
    return TimestampService.isValidDate(date) ? date.toISOString() : null;
  }

  const parsed = TimestampService.parseDateTimeInput(value);
  return TimestampService.isValidDate(parsed) ? parsed.toISOString() : null;
}

function toDate(value: string | null | undefined): Date | null {
  if (value === null || value === undefined || value.length === 0) {
    return null;
  }

  const parsed = TimestampService.parseDateTimeInput(value);
  return TimestampService.isValidDate(parsed) ? parsed : null;
}

function isCouponExpired(expiryDate: string | null): boolean {
  if (expiryDate === null || expiryDate.length === 0) {
    return false;
  }

  const expiry = TimestampService.parseDateTimeInput(expiryDate);
  if (!TimestampService.isValidDate(expiry)) {
    return false;
  }

  return TimestampService.getMillisecondsSince(expiry) > 0;
}

function calculateDaysUntilExpiry(expiryDate: string | null): number | null {
  if (expiryDate === null || expiryDate.length === 0) {
    return null;
  }

  const expiry = TimestampService.parseDateTimeInput(expiryDate);
  if (!TimestampService.isValidDate(expiry)) {
    return null;
  }

  const millisecondsUntilExpiry = -TimestampService.getMillisecondsSince(expiry);
  const daysUntilExpiry = Math.ceil(millisecondsUntilExpiry / (1000 * 60 * 60 * 24));

  return daysUntilExpiry > 0 ? daysUntilExpiry : 0;
}

export function transformMerchantCoupon(backendCoupon: MerchantCouponApi): TransformedMerchantCoupon {
  const createdAt = toIsoString(backendCoupon.created_at) ?? '';
  const updatedAt = toIsoString(backendCoupon.updated_at) ?? '';
  const expiresAt = toIsoString(backendCoupon.expires_at);

  return {
    ...backendCoupon,
    couponId: backendCoupon.coupon_id,
    couponTitle: backendCoupon.title,
    couponDescription: backendCoupon.description,
    couponType: backendCoupon.coupon_type,
    couponValue: backendCoupon.coupon_value,
    couponMinimumAmount: backendCoupon.minimum_amount,
    couponIsActive: backendCoupon.is_active,
    couponCreatedAt: createdAt,
    couponUpdatedAt: updatedAt,
    couponExpiredAt: expiresAt,
    id: backendCoupon.coupon_id,
    name: backendCoupon.title,
    discountType: backendCoupon.coupon_type,
    discount: backendCoupon.coupon_value,
    isActive: backendCoupon.is_active,
    expiryDate: expiresAt,
    code: backendCoupon.title,
    isPending: false,
    isExpired: isCouponExpired(expiresAt),
    isFullyRedeemed: false,
    daysUntilExpiry: calculateDaysUntilExpiry(expiresAt),
    currentRedemptions: 0,
    maxRedemptions: undefined,
    redemptionPercentage: 0,
  };
}

export function transformUserCoupon(backendCoupon: TransformedUserCoupon): TransformedUserCoupon {
  return backendCoupon;
}

export function transformMerchantCoupons(
  backendCoupons: ListCouponsResponseType | MerchantCouponApi[] | null | undefined,
): TransformedMerchantCoupon[] {
  if (!backendCoupons) {
    return [];
  }

  if (Array.isArray(backendCoupons)) {
    return backendCoupons.map(transformMerchantCoupon);
  }

  if (hasDataArray<MerchantCouponApi>(backendCoupons)) {
    return backendCoupons.data.map(transformMerchantCoupon);
  }

  return [];
}

export function transformUserCoupons(
  backendCoupons: ListWalletCouponsResponseType | TransformedUserCoupon[] | null | undefined,
): TransformedUserCoupon[] {
  if (!backendCoupons) {
    return [];
  }

  if (Array.isArray(backendCoupons)) {
    return backendCoupons.map(transformUserCoupon);
  }

  if (hasDataArray<TransformedUserCoupon>(backendCoupons)) {
    return backendCoupons.data.map(transformUserCoupon);
  }

  return [];
}

export function transformMerchant(backendMerchant: MerchantMembershipApi): TransformedMerchant {
  const memberSince = toDate(backendMerchant.member_since) ?? TimestampService.getCurrentDate();

  return {
    ...backendMerchant,
    merchantId: backendMerchant.merchant_id,
    merchantName: backendMerchant.merchant_name,
    logoUrl: backendMerchant.logo_url,
    memberSince,
    creditBalance: backendMerchant.credit_balance,
    availablePoints: backendMerchant.available_points,
  };
}

export function transformMerchants(
  backendMerchants: GetUserMerchantsResponseType | MerchantMembershipApi[] | null | undefined,
): TransformedMerchant[] {
  if (!backendMerchants) {
    return [];
  }

  if (Array.isArray(backendMerchants)) {
    return backendMerchants.map(transformMerchant);
  }

  if (hasDataArray<MerchantMembershipApi>(backendMerchants)) {
    return backendMerchants.data.map(transformMerchant);
  }

  return [];
}

export function transformMerchantMember(backendMember: MerchantMemberApi): TransformedMember {
  const memberSince = toDate(backendMember.member_since);

  return {
    ...backendMember,
    userId: backendMember.user_id,
    displayName: backendMember.display_name,
    phoneMasked: backendMember.phone_masked,
    role: backendMember.role,
    creditBalance: backendMember.credit_balance,
    pointsBalance: backendMember.points_balance,
    memberSince,
    createdAt: memberSince,
    status: 'active',
  };
}

export function transformMerchantMembers(
  backendMembers: ListMembersResponseType | MerchantMemberApi[] | null | undefined,
): TransformedMember[] {
  if (!backendMembers) {
    return [];
  }

  if (Array.isArray(backendMembers)) {
    return backendMembers.map(transformMerchantMember);
  }

  if (hasDataArray<MerchantMemberApi>(backendMembers)) {
    return backendMembers.data.map(transformMerchantMember);
  }

  return [];
}

export function transformCouponRedemption(
  backendRedemption: TransformedCouponRedemption,
): TransformedCouponRedemption {
  return backendRedemption;
}

export function transformCouponRedemptions(
  backendRedemptions:
    | ListRedemptionsResponseType
    | TransformedCouponRedemption[]
    | null
    | undefined,
): TransformedCouponRedemption[] {
  if (!backendRedemptions) {
    return [];
  }

  if (Array.isArray(backendRedemptions)) {
    return backendRedemptions.map(transformCouponRedemption);
  }

  if (hasDataArray<TransformedCouponRedemption>(backendRedemptions)) {
    return backendRedemptions.data.map(transformCouponRedemption);
  }

  return [];
}

export function transformToLegacyCouponFormat(backendCoupon: TransformedMerchantCoupon) {
  return {
    id: backendCoupon.couponId,
    name: backendCoupon.couponTitle,
    description: backendCoupon.couponDescription,
    discountType: backendCoupon.couponType,
    discount: backendCoupon.couponValue,
    minimumAmount: backendCoupon.couponMinimumAmount,
    isActive: backendCoupon.couponIsActive,
    createdAt: backendCoupon.couponCreatedAt,
    updatedAt: backendCoupon.couponUpdatedAt,
    expiryDate: backendCoupon.couponExpiredAt,
    commissionPercentage: 0,
  };
}

export function hasComputedProperties(coupon: unknown): coupon is CouponWithComputed {
  return typeof coupon === 'object' && coupon !== null && '_computed' in coupon;
}

export function getComputedProperty<T>(
  coupon: TransformedMerchantCoupon,
  property: string,
  defaultValue: T,
): T {
  const computed = (coupon as CouponWithComputed)._computed;

  if (
    computed &&
    typeof computed === 'object' &&
    property in computed
  ) {
    return computed[property] as T;
  }

  return defaultValue;
}
